import type { search as searchContracts } from '@personal-blog/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch, type ApiRequestError } from '@/lib/api';

export type SearchScope = searchContracts.SearchScope;
export type SearchHit = searchContracts.SearchHit;
export type SearchSuggestion = searchContracts.SearchSuggestion;
export type SearchResponse = searchContracts.SearchResponse;
export type SearchSuggestionsResponse = searchContracts.SearchSuggestionsResponse;

export interface SearchFilter {
  q: string;
  scope?: SearchScope;
  limit?: number;
  offset?: number;
}

export const searchKeys = {
  all: ['search'] as const,
  results: (filter: SearchFilter) => ['search', 'results', cleanFilter(filter)] as const,
  suggestions: (filter: SearchFilter) => ['search', 'suggestions', cleanFilter(filter)] as const,
};

function cleanFilter(filter: SearchFilter): SearchFilter {
  const q = filter.q.trim();
  return {
    q,
    ...(filter.scope !== undefined && filter.scope !== 'all' ? { scope: filter.scope } : {}),
    ...(filter.limit !== undefined ? { limit: filter.limit } : {}),
    ...(filter.offset !== undefined ? { offset: filter.offset } : {}),
  };
}

function searchPath(base: '/api/search' | '/api/search/suggestions', filter: SearchFilter): string {
  const cleaned = cleanFilter(filter);
  const usp = new URLSearchParams();
  usp.set('q', cleaned.q);
  if (cleaned.scope !== undefined) usp.set('scope', cleaned.scope);
  if (cleaned.limit !== undefined) usp.set('limit', String(cleaned.limit));
  if (cleaned.offset !== undefined) usp.set('offset', String(cleaned.offset));
  return `${base}?${usp.toString()}`;
}

function enabledByQuery(filter: SearchFilter, enabled: boolean | undefined): boolean {
  return (enabled ?? true) && filter.q.trim().length >= 2;
}

export function useSearch(
  filter: SearchFilter,
  opts: { enabled?: boolean } = {},
): UseQueryResult<SearchResponse, ApiRequestError> {
  return useQuery({
    queryKey: searchKeys.results(filter),
    queryFn: () => apiFetch<SearchResponse>(searchPath('/api/search', filter)),
    enabled: enabledByQuery(filter, opts.enabled),
  });
}

export function useSearchSuggestions(
  filter: SearchFilter,
  opts: { enabled?: boolean } = {},
): UseQueryResult<SearchSuggestionsResponse, ApiRequestError> {
  return useQuery({
    queryKey: searchKeys.suggestions(filter),
    queryFn: () =>
      apiFetch<SearchSuggestionsResponse>(searchPath('/api/search/suggestions', filter)),
    enabled: enabledByQuery(filter, opts.enabled),
  });
}
